import React, { useState, useEffect } from "react";
import { Icon } from "@iconify/react";
import { Typography } from "../../../Components/Typography";
import Card from "../../../Components/Card";
import Button from "../../../Components/Button";
import { updateUser } from "../../../Utils/apiServices";
import { useAuth } from "../../../Context/authContext";

const PreferencesTab = ({ initialFormData, onUpdate }) => {
  const { user, updateUser: updateAuthUser } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState({ type: "", text: "" });
  const [maximumEngagements, setMaximumEngagements] = useState(
    initialFormData?.maximum_engagements_assigned || user?.maximum_engagements_assigned || 3
  );

  useEffect(() => {
    if (initialFormData?.maximum_engagements_assigned !== undefined) {
      setMaximumEngagements(initialFormData.maximum_engagements_assigned);
    } else if (user?.maximum_engagements_assigned !== undefined) {
      setMaximumEngagements(user.maximum_engagements_assigned);
    }
  }, [initialFormData, user]);

  const handleSave = async () => {
    if (!user?.id) {
      setSaveMessage({ type: "error", text: "User ID not found. Please log in again." });
      return;
    }

    setIsSaving(true);
    setSaveMessage({ type: "", text: "" });

    try {
      const apiData = {
        maximum_engagements_assigned: maximumEngagements ? parseInt(maximumEngagements) : null,
      };

      const response = await updateUser(user.id, apiData);

      if (response.success) {
        const updatedUserData = {
          maximum_engagements_assigned: apiData.maximum_engagements_assigned,
        };
        
        updateAuthUser(updatedUserData);

        const profileToSave = {
          ...initialFormData,
          maximum_engagements_assigned: apiData.maximum_engagements_assigned,
        };
        
        if (onUpdate) {
          onUpdate(profileToSave);
        }

        setSaveMessage({ type: "success", text: "Preferences updated successfully!" });
        
        setTimeout(() => {
          setSaveMessage({ type: "", text: "" });
        }, 3000);
      } else {
        setSaveMessage({ 
          type: "error", 
          text: response.error || "Failed to update preferences. Please try again." 
        });
      }
    } catch (error) {
      console.error("Error updating preferences:", error);
      setSaveMessage({ 
        type: "error", 
        text: error.error || "An error occurred while updating your preferences." 
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    setMaximumEngagements(
      initialFormData?.maximum_engagements_assigned || user?.maximum_engagements_assigned || 3
    );
    setSaveMessage({ type: "", text: "" });
  };

  // Generate options from 1 to 10
  const engagementOptions = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-6">
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-1">
            <Typography as="h2" className="text-lg font-bold text-primary-900">
              Preferences
            </Typography>
            <Typography as="p" className="text-sm text-tertiary-600">
              Update your engagement preferences here.
            </Typography>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="secondary"
              onClick={handleCancel}
              disabled={isSaving}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={isSaving}
              loading={isSaving}
              className="bg-purple-600 hover:bg-purple-700 focus:ring-purple-500"
            >
              Save
            </Button>
          </div>
        </div>

        {saveMessage.text && (
          <div
            className={`p-4 rounded-lg border ${
              saveMessage.type === "success"
                ? "bg-green-50 border-green-200 text-green-800"
                : "bg-red-50 border-red-200 text-red-800"
            }`}
          >
            <div className="flex items-center gap-2">
              <Icon
                icon={
                  saveMessage.type === "success"
                    ? "lucide:check-circle"
                    : "lucide:alert-circle"
                }
                className={`h-5 w-5 ${
                  saveMessage.type === "success"
                    ? "text-green-600"
                    : "text-red-600"
                }`}
              />
              <Typography as="p" className="text-sm font-medium">
                {saveMessage.text}
              </Typography>
            </div>
          </div>
        )}

        <div className="flex flex-col gap-6">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <Typography as="label" className="text-sm font-medium text-primary-900 block mb-2">
                Maximum Engagements Allotted
              </Typography>
              <div className="relative">
                <select
                  value={maximumEngagements || ""}
                  onChange={(e) => setMaximumEngagements(e.target.value ? parseInt(e.target.value) : null)}
                  className="w-full rounded-lg border border-slate-200 bg-white px-4 pr-10 py-2.5 text-sm text-slate-900 shadow-sm focus:border-brand-blue focus:outline-none focus:ring-2 focus:ring-brand-blue appearance-none"
                >
                  {engagementOptions.map((num) => (
                    <option key={num} value={num}>
                      {num}
                    </option>
                  ))}
                </select>
                <Icon
                  icon="lucide:chevron-down"
                  className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default PreferencesTab;

